"use client";

import { useEffect, useRef } from "react";
import { CURA_MARK_DOTS } from "@/lib/cura-mark";

/**
 * The Bloom Field — a full-bleed, pointer-reactive canvas where the real
 * 28-dot Cura mark sits at centre and its dot language continues outward in
 * generated rings until it dissolves into the cream. Plain Canvas 2D + spring
 * physics (no WebGL dependency): idle drift, pointer repulsion with springy
 * return, ring-depth parallax, a blush/sage lumen pair chasing the cursor,
 * proximity colour-warming, a heartbeat core, and click shockwaves.
 * Used by /login-v2. Honors prefers-reduced-motion (static render).
 */

interface Dot {
  hx: number; // home
  hy: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  ring: number;
  alpha: number;
  phase: number;
  /** Entrance stagger (ms) — the bloom opens outward ring by ring. */
  delay: number;
  /** Set when the player actually clicks ON this dot (the catch game). */
  caughtAt?: number | undefined;
}

interface Toast {
  x: number;
  y: number;
  t0: number;
  text: string;
}

interface Ripple {
  x: number;
  y: number;
  t0: number;
}

const INK = [40, 3, 15] as const;
const BLUSH = [201, 134, 164] as const;

/* The catch-game voice. Layered by rarity so the cheeky lines carry the
   rhythm and the soft brand lines stay special:
   core dot > milestone count > rare (~1/50) > time-of-day (~1/6 when one
   applies) > soft brand (~1/8) > the standard pool with anti-repeat. */
const CATCH_LINES = [
  // Quick hits
  "Caught one!",
  "Gotcha.",
  "Quick hands.",
  "Sharp eye.",
  "Clean catch.",
  "Nothing gets past you.",
  "Snagged.",
  "Show-off.",
  "Too easy?",
  "Didn't even blink.",
  // Clinic flavoured
  "Steady as a surgeon.",
  "Laser focus. Literally.",
  "Spot check: passed.",
  "Gentle touch, confirmed.",
  "Diagnosis: excellent aim.",
  "That dot needed a consult anyway.",
  "Booked that one right in.",
  "Front desk reflexes.",
  "Flawless technique.",
  // The dots have feelings
  "The dots talk about you now.",
  "It never saw you coming.",
  "The others are hiding now.",
  "That one was my favourite.",
  "They're getting nervous.",
  "It was resting.",
  "Word spreads fast out here.",
  "That one had plans.",
  "The field remembers.",
];
const CATCH_SOFT = [
  "You caught a little light.",
  "A lumen, for you.",
  "Bloom, interrupted.",
  "Careful, they're delicate.",
];
const CATCH_RARE = "This never happens.";
// The heartbeat core has a story arc: found, then fond.
const CATCH_CORE = [
  "You found the heart of Cura.",
  "The heart skips a beat.",
  "Still beating. Still yours.",
];
const CATCH_MILESTONES: Record<number, string> = {
  3: "Three. Warming up.",
  5: "Five! Precision is your thing.",
  10: "Ten dots. Go run the clinic.",
  15: "Fifteen. The dots have called a meeting.",
  20: "Twenty. Should we be worried?",
  25: "Twenty-five. You can stop now. (Don't.)",
  30: "Thirty?! The field surrenders.",
  50: "Fifty. A legend logs in.",
};
/** Staff sign in at the same hours every day — let the field notice. */
function catchTimeLine(): string | null {
  const d = new Date();
  const h = d.getHours();
  if (h < 10) return "Caught one before coffee.";
  if (h >= 21) return "Night shift precision.";
  if (d.getDay() === 5) return "Friday form.";
  return null;
}

/** Seeded PRNG (mulberry32) — every rebuild must produce the IDENTICAL
 *  field, or resizing reshuffles the universe and reads as jumpy. */
function mulberry32(a: number) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function BloomField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    // Explicitly typed rebinding so the narrowing survives into the hoisted
    // function declarations below (TS drops guard-narrowing there).
    const canvas: HTMLCanvasElement = el;
    const context = canvas.getContext("2d");
    if (!context) return;
    const ctx: CanvasRenderingContext2D = context;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0;
    let H = 0;
    let cx = 0;
    let cy = 0;
    let raf = 0;
    let dots: Dot[] = [];
    const ripples: Ripple[] = [];
    const toasts: Toast[] = [];
    let catches = 0;
    let coreCatches = 0;
    let lastLine = -1;
    // Hold-the-heartbeat: rest the cursor on the core for 3s and the whole
    // field pulses once in sync. Re-arms only after the pointer leaves.
    let coreDot: Dot | null = null;
    let holdMs = 0;
    let holdArmed = true;
    let lastT = -1;
    let fieldBeatT0 = -1;
    // The idle line: 30s without a pointer move (or away in another tab),
    // then movement — the field noticed. Once per page load.
    let lastMoveT = performance.now();
    let idleShown = false;
    // Pointer: raw target + two spring followers (lead lumen, lagging lumen).
    const ptr = { x: -9e3, y: -9e3, on: false, lx: -9e3, ly: -9e3, sx: -9e3, sy: -9e3 };

    function build() {
      const prev = dots;
      const rand = mulberry32(19_02);
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      // The canvas now spans the WHOLE page (the panel is frosted glass laid
      // over the field) — anchor the mark in the visible field area.
      const split = W > 860;
      cx = split ? W * 0.3 : W * 0.5;
      cy = split ? H * 0.5 : Math.min(H * 0.34, 320);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      dots = [];
      const scale = (Math.min(W > 860 ? W * 0.58 : W, H) * 0.72) / 512;

      // The real mark, faithfully, at centre.
      for (const [mx, my, mr, ring] of CURA_MARK_DOTS) {
        dots.push({
          hx: cx + (mx - 256) * scale,
          hy: cy + (my - 256) * scale,
          x: cx + (mx - 256) * scale * 0.55,
          y: cy + (my - 256) * scale * 0.55,
          vx: 0,
          vy: 0,
          r: mr * scale,
          ring,
          alpha: ring === 0 ? 0.92 : ring === 1 ? 0.88 : 0.8,
          phase: rand() * Math.PI * 2,
          delay: ring * 130,
        });
      }

      // Continue the mark's language outward until it leaves the panel:
      // rings of dots, ~even arc spacing, sizes shrinking, opacity dissolving.
      const maxR = Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy)) + 60;
      let radius = 191 * scale + 68 * scale;
      let size = 13.5 * scale;
      let ring = 3;
      while (radius < maxR && ring < 40) {
        const n = Math.max(12, Math.round((2 * Math.PI * radius) / (58 * scale)));
        const offset = (ring % 2) * (Math.PI / n) + ring * 0.07;
        const fade = Math.max(0.028, 0.58 * Math.pow(0.845, ring - 2));
        for (let i = 0; i < n; i++) {
          const a = offset + (i / n) * Math.PI * 2;
          const jitterR = radius + (rand() - 0.5) * 6 * scale;
          dots.push({
            hx: cx + Math.cos(a) * jitterR,
            hy: cy + Math.sin(a) * jitterR,
            x: cx + Math.cos(a) * jitterR * 0.82,
            y: cy + Math.sin(a) * jitterR * 0.82,
            vx: 0,
            vy: 0,
            r: Math.max(1.1, size * (0.72 + rand() * 0.35)),
            ring,
            alpha: fade,
            phase: rand() * Math.PI * 2,
            delay: 260 + (ring - 2) * 85,
          });
        }
        radius += 58 * scale;
        size *= 0.9;
        ring++;
      }

      // Continuity across rebuilds: dots keep their position + velocity and
      // the springs glide them to their new homes — the layout flows instead
      // of snapping. (Entrance choreography only applies to the first build.)
      if (prev.length > 0) {
        const n = Math.min(prev.length, dots.length);
        for (let i = 0; i < n; i++) {
          dots[i]!.x = prev[i]!.x;
          dots[i]!.y = prev[i]!.y;
          dots[i]!.vx = prev[i]!.vx;
          dots[i]!.vy = prev[i]!.vy;
        }
        // Any newcomers (bigger window → more fringe rings) appear in place.
        for (let i = n; i < dots.length; i++) {
          dots[i]!.x = dots[i]!.hx;
          dots[i]!.y = dots[i]!.hy;
        }
      }
      coreDot = dots.find((d) => d.ring === 0) ?? null;
    }

    function lumen(x: number, y: number, radius: number, rgb: readonly number[], a: number) {
      const g = ctx!.createRadialGradient(x, y, 0, x, y, radius);
      g.addColorStop(0, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`);
      g.addColorStop(1, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0)`);
      ctx!.fillStyle = g;
      ctx!.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }

    let born = -1;

    function frame(t: number) {
      // Schedule FIRST so a one-frame exception can never kill the loop
      // (a thrown frame used to freeze the whole field permanently).
      raf = requestAnimationFrame(frame);
      if (born < 0) born = t;
      const age = t - born;
      // The whole field breathes — a ~1% radial swell over ~5s.
      const breath = reduced ? 1 : 1 + 0.011 * Math.sin((t / 5200) * Math.PI * 2);
      ctx!.clearRect(0, 0, W, H);

      // Pointer springs — the lead lumen chases fast, the sage one drifts after.
      ptr.lx += (ptr.x - ptr.lx) * 0.14;
      ptr.ly += (ptr.y - ptr.ly) * 0.14;
      ptr.sx += (ptr.x - ptr.sx) * 0.05;
      ptr.sy += (ptr.y - ptr.sy) * 0.05;

      if (ptr.on && !reduced) {
        lumen(ptr.lx, ptr.ly, 320, [232, 180, 200], 0.22);
        lumen(ptr.sx, ptr.sy, 420, [159, 194, 180], 0.18);
      }

      // Parallax offset from centre, deeper for outer rings.
      const pxn = ptr.on ? (ptr.sx - cx) / Math.max(W, 1) : 0;
      const pyn = ptr.on ? (ptr.sy - cy) / Math.max(H, 1) : 0;

      const beat = reduced
        ? 1
        : 1 +
          0.14 * Math.max(0, Math.sin((t / 1900) * Math.PI * 2)) *
            Math.max(0, Math.sin((t / 1900) * Math.PI * 4));

      // Hold the heartbeat — cursor resting on the core (at its live,
      // squirming position) charges over 3s, blushing the core as it goes.
      const dt = lastT < 0 ? 16 : Math.min(64, t - lastT);
      lastT = t;
      let holdFrac = 0;
      if (!reduced && ptr.on && coreDot) {
        const onCore = Math.hypot(ptr.x - coreDot.x, ptr.y - coreDot.y) < coreDot.r + 6;
        if (onCore && holdArmed) {
          holdMs += dt;
          holdFrac = Math.min(1, holdMs / 3000);
          if (holdMs >= 3000) {
            holdArmed = false;
            holdMs = 0;
            fieldBeatT0 = t;
            toasts.push({
              x: coreDot.x,
              // Above the ring of nine (which swells with the pulse), in the
              // quiet band before the halo — not jammed against the core.
              y: coreDot.y - coreDot.r * 2.8,
              t0: t,
              text: "The whole field beats as one.",
            });
          }
        } else if (!onCore) {
          holdMs = 0;
          holdArmed = true;
        }
      } else {
        holdMs = 0;
      }
      // The reward: one synchronized lub-dub across every dot in the field.
      let fieldBeat = 0;
      if (fieldBeatT0 >= 0) {
        const fAge = t - fieldBeatT0;
        if (fAge > 1300) fieldBeatT0 = -1;
        else
          fieldBeat =
            0.3 * Math.exp(-Math.pow((fAge - 180) / 130, 2)) +
            0.2 * Math.exp(-Math.pow((fAge - 560) / 150, 2));
      }

      for (const d of dots) {
        const depth = d.ring <= 2 ? d.ring * 0.05 : Math.min(1.6, 0.2 + (d.ring - 2) * 0.11);

        // Idle drift + parallax + breath define this frame's resting target.
        const driftA = reduced ? 0 : 1.4 + depth * 1.6;
        const bhx = cx + (d.hx - cx) * breath;
        const bhy = cy + (d.hy - cy) * breath;
        const tx =
          bhx + (reduced ? 0 : Math.sin(t / 1400 + d.phase) * driftA) - pxn * 26 * depth;
        const ty =
          bhy + (reduced ? 0 : Math.cos(t / 1700 + d.phase * 1.3) * driftA) - pyn * 26 * depth;

        if (!reduced) {
          // Pointer repulsion — dots part around the cursor.
          if (ptr.on) {
            const dx = d.x - ptr.lx;
            const dy = d.y - ptr.ly;
            const dist = Math.hypot(dx, dy);
            const R = 150;
            if (dist < R && dist > 0.001) {
              const f = Math.pow(1 - dist / R, 2) * 3.2;
              d.vx += (dx / dist) * f;
              d.vy += (dy / dist) * f;
            }
          }
          // Click shockwaves — an expanding band kicks dots outward.
          // Ages are clamped ≥0 everywhere: pointer events stamp t0 with
          // performance.now(), which can sit a few ms AHEAD of this frame's
          // rAF timestamp — a negative age meant a negative arc radius,
          // which threw and froze the field.
          for (const rp of ripples) {
            const age = Math.max(0, t - rp.t0);
            const waveR = age * 0.55;
            const dx = d.x - rp.x;
            const dy = d.y - rp.y;
            const dist = Math.hypot(dx, dy);
            const band = Math.abs(dist - waveR);
            if (band < 46 && dist > 0.001 && age < 1400) {
              const f = (1 - band / 46) * 2.6 * Math.exp(-age / 900);
              d.vx += (dx / dist) * f;
              d.vy += (dy / dist) * f;
            }
          }
          // Spring home + damping.
          d.vx += (tx - d.x) * 0.055;
          d.vy += (ty - d.y) * 0.055;
          d.vx *= 0.86;
          d.vy *= 0.86;
          d.x += d.vx;
          d.y += d.vy;
        } else {
          d.x = tx;
          d.y = ty;
        }

        // Proximity warming — nearby dots blush. A caught dot flushes fully.
        let warm = 0;
        if (ptr.on && !reduced) {
          const dist = Math.hypot(d.x - ptr.lx, d.y - ptr.ly);
          if (dist < 220) warm = Math.pow(1 - dist / 220, 1.6) * 0.75;
        }
        // Charging feedback while the heartbeat hold builds.
        if (d === coreDot && holdFrac > 0) warm = Math.max(warm, holdFrac * 0.85);
        let caughtPulse = 0;
        if (d.caughtAt !== undefined) {
          const cAge = Math.max(0, t - d.caughtAt);
          if (cAge < 900) caughtPulse = Math.exp(-cAge / 260);
          else d.caughtAt = undefined;
        }
        const m = Math.max(warm, caughtPulse);
        const rr = INK[0] + (BLUSH[0] - INK[0]) * m;
        const gg = INK[1] + (BLUSH[1] - INK[1]) * m;
        const bb = INK[2] + (BLUSH[2] - INK[2]) * m;

        // Entrance: each ring fades in as it travels outward to its home.
        const ramp = reduced ? 1 : Math.min(1, Math.max(0, (age - d.delay) / 460));
        if (ramp <= 0) continue;
        const eased = 1 - Math.pow(1 - ramp, 2);

        const radius =
          (d.ring === 0 ? d.r * beat : d.r) * (1 + 0.45 * caughtPulse) * (1 + fieldBeat);
        ctx!.beginPath();
        ctx!.arc(d.x, d.y, radius, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${rr | 0},${gg | 0},${bb | 0},${d.alpha * eased})`;
        ctx!.fill();
      }

      // Catch toasts — a line of microcopy rises from the catch and fades.
      if (toasts.length > 0) {
        // Inherit the page face (AD Sans on /login) via the canvas's computed
        // style; fall back to the system stack.
        const fam =
          getComputedStyle(canvas).fontFamily ||
          "-apple-system, BlinkMacSystemFont, 'DM Sans', sans-serif";
        ctx!.font = `500 14px ${fam}`;
        ctx!.textAlign = "center";
        for (let i = toasts.length - 1; i >= 0; i--) {
          const to = toasts[i]!;
          const age = Math.max(0, t - to.t0);
          if (age > 1700) {
            toasts.splice(i, 1);
            continue;
          }
          const fadeIn = Math.min(1, age / 140);
          const fadeOut = age > 1100 ? 1 - (age - 1100) / 600 : 1;
          const alpha = fadeIn * Math.max(0, fadeOut);
          const ty = to.y - age * 0.022;
          // Cream halo so the line reads over any dots.
          ctx!.shadowColor = "rgba(252,250,248,0.95)";
          ctx!.shadowBlur = 10;
          ctx!.fillStyle = `rgba(40,3,15,${0.9 * alpha})`;
          ctx!.fillText(to.text, to.x, ty);
          ctx!.fillText(to.text, to.x, ty);
          ctx!.shadowBlur = 0;
        }
      }

      // Ripple rings (visual) + retire old ones.
      for (let i = ripples.length - 1; i >= 0; i--) {
        const rp = ripples[i]!;
        const age = Math.max(0, t - rp.t0);
        if (age > 1400) {
          ripples.splice(i, 1);
          continue;
        }
        const waveR = age * 0.55;
        ctx!.beginPath();
        ctx!.arc(rp.x, rp.y, waveR, 0, Math.PI * 2);
        ctx!.strokeStyle = `rgba(40,3,15,${0.16 * (1 - age / 1400)})`;
        ctx!.lineWidth = 1.2;
        ctx!.stroke();
      }
      // (next frame is scheduled at the top of frame() — crash-proofing)
    }

    function onMove(e: PointerEvent) {
      // Window-level so the lumen keeps following the cursor over the frosted
      // panel (the canvas sits beneath it).
      const rect = canvas.getBoundingClientRect();
      ptr.x = e.clientX - rect.left;
      ptr.y = e.clientY - rect.top;
      const nowT = performance.now();
      if (!reduced && !idleShown && nowT - lastMoveT > 30_000) {
        idleShown = true;
        toasts.push({
          // Clamped into the open field so it never renders under the panel.
          x: Math.min(Math.max(ptr.x, 90), W > 860 ? W * 0.6 : W - 90),
          y: Math.max(ptr.y - 26, 70),
          t0: nowT,
          text: "We thought you'd left.",
        });
      }
      lastMoveT = nowT;
      if (!ptr.on) {
        ptr.on = true;
        ptr.lx = ptr.sx = ptr.x;
        ptr.ly = ptr.sy = ptr.y;
      }
    }
    function onLeave() {
      ptr.on = false;
    }
    function onClick(e: PointerEvent) {
      if (reduced) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const now = performance.now();
      ripples.push({ x, y, t0: now });

      // The catch game: did the click land ON a dot (at its current,
      // physics-displaced position)? 2px of forgiveness.
      let hit: Dot | null = null;
      let best = 2;
      for (const d of dots) {
        const over = Math.hypot(d.x - x, d.y - y) - d.r;
        if (over < best) {
          best = over;
          hit = d;
        }
      }
      if (hit) {
        catches++;
        hit.caughtAt = now;
        // The count rides on the canvas so the sign-in button can read it
        // for the send-off line (components/login/clinichub-button.tsx).
        canvas.dataset.catches = String(catches);
        // The neighbours saw it happen — a startled scatter with a little
        // panicked jitter, then the springs settle everyone back down.
        for (const d of dots) {
          if (d === hit) continue;
          const fdx = d.x - hit.x;
          const fdy = d.y - hit.y;
          const fdist = Math.hypot(fdx, fdy);
          if (fdist < 90 && fdist > 0.001) {
            const f = (1 - fdist / 90) * 5;
            d.vx += (fdx / fdist) * f + (Math.random() - 0.5) * 1.2;
            d.vy += (fdy / fdist) * f + (Math.random() - 0.5) * 1.2;
          }
        }
        const timely = catchTimeLine();
        const text =
          hit.ring === 0
            ? CATCH_CORE[Math.min(coreCatches++, CATCH_CORE.length - 1)]!
            : (CATCH_MILESTONES[catches] ??
              (Math.random() < 1 / 50
                ? CATCH_RARE
                : timely && Math.random() < 1 / 6
                  ? timely
                  : Math.random() < 1 / 8
                    ? CATCH_SOFT[Math.floor(Math.random() * CATCH_SOFT.length)]!
                    : (() => {
                        let i = Math.floor(Math.random() * CATCH_LINES.length);
                        if (i === lastLine) i = (i + 1) % CATCH_LINES.length;
                        lastLine = i;
                        return CATCH_LINES[i]!;
                      })()));
        toasts.push({ x, y: y - hit.r - 10, t0: now, text });
      }
    }

    build();
    // Debounced: during a drag-resize the canvas raster just stretches
    // (smooth); the field rebuilds once the size settles.
    let resizeT: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      if (resizeT) clearTimeout(resizeT);
      resizeT = setTimeout(build, 160);
    });
    ro.observe(canvas);
    window.addEventListener("pointermove", onMove);
    document.documentElement.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("pointerdown", onClick);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      if (resizeT) clearTimeout(resizeT);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("pointerdown", onClick);
    };
  }, []);

  return <canvas ref={canvasRef} className="lv2-canvas" aria-hidden="true" />;
}
